import { inject, Injectable } from '@angular/core';
import {
  EStopStatus,
  EVehicleType,
  ICoords,
  IFreightBasicForCarrier,
  IFreightDetailedForCarrier,
  IFreightVehicle,
  IStopDetailedForCarrier,
  IStopGoodsInfo,
  UUID,
} from '@okcargo-apps/shared-domain';
import { FreightCarrierCommandService } from '@okcargo-apps/shared-infrastructure';
import {
  ArriveFreightStopCommand,
  DepartFreightStopCommand,
  DriverPayload,
  RequestPayInAdvanceCommand,
  UpdateFreightDriverAndVehiclesAsCarrierCommand,
  UploadFreightDocumentCommand,
  VehiclePayload,
  VerifyFreightStopGoodsInfoCommand,
} from '@okcargo/command-processor';
import { IAssignationDriverOption, IAssignationVehicleOption } from './freight-carrier-store.model';

@Injectable({
  providedIn: 'root',
})
export class FreightCarrierMutationsService {
  private readonly freightCarrierCommandService = inject(FreightCarrierCommandService);

  // In old app: async markDepartureDateTime(stop: FreightStopResponse) { ...
  public async modifyStopStatus(
    stop: IStopDetailedForCarrier,
    freight: IFreightDetailedForCarrier,
    nextStopStatus: EStopStatus,
    coords?: ICoords,
  ): Promise<void> {
    // old markDepartureDateTime() and markArrivalDateTime()

    const trailerVehicle: IFreightVehicle | null =
      freight.vehicles?.find((vehicle) => vehicle.type === EVehicleType.TRAILER) || null;
    const motorizedVehicle: IFreightVehicle | null =
      freight.vehicles?.find((vehicle) => vehicle.type !== EVehicleType.TRAILER) || null;

    const command = {
      freightId: freight.id,
      stopId: stop.id,
      driverPayload: {} as DriverPayload, // Required by backend even when empty
    } as ArriveFreightStopCommand;

    if (coords) {
      command.driverAppPointPayload = coords;
    }

    if (motorizedVehicle) {
      command.motorizedVehiclePayload = {
        id: motorizedVehicle.id,
        plateNumber: motorizedVehicle.plate,
      } as unknown as VehiclePayload;
    }
    if (trailerVehicle) {
      command.trailerVehiclePayload = {
        id: trailerVehicle.id,
        plateNumber: trailerVehicle.plate,
      } as unknown as VehiclePayload;
    }

    if (nextStopStatus === EStopStatus.STARTED) {
      await this.handleCommand(this.freightCarrierCommandService.arriveToFreightStop(command));
    } else if (nextStopStatus === EStopStatus.FINISHED) {
      await this.handleCommand(
        this.freightCarrierCommandService.departFromFreightStop(command as DepartFreightStopCommand),
      );
    }
  }

  // In old app: async sendData() { ... in freight-data.component.ts
  public async sendStopGoodsInfo(command: IStopGoodsInfo): Promise<void> {
    await this.handleCommand(
      this.freightCarrierCommandService.verifyFreightStopGoodsInfo(command as VerifyFreightStopGoodsInfoCommand),
    );
  }

  public async uploadFreightDeliveryNote(fileInput: File, stopId: string, freightId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (readerEvent: ProgressEvent<FileReader>): Promise<void> => {
        const fileData = (readerEvent.target as FileReader).result as string;
        const fileType = fileInput.type;
        const fileName = fileInput.name;
        const extension = fileName.substring(fileName.lastIndexOf('.'));
        const base64Data = fileData.substring(fileData.indexOf(',') + 1);
        const uploadFileName = 'order_' + this.getFileFormattedDate(new Date()) + extension;

        const command: UploadFreightDocumentCommand = {
          data: base64Data,
          contentType: fileType,
          description: '',
          documentId: crypto.randomUUID(),
          freightId: freightId,
          name: uploadFileName,
          type: 'DELIVERY_NOTE',
          freightStopId: stopId,
        } as unknown as UploadFreightDocumentCommand;

        try {
          await this.freightCarrierCommandService.uploadFreightDocument(command);
          resolve(fileName);
        } catch (error) {
          console.warn('File not saved ' + JSON.stringify(error));
          reject(error);
        }
      };

      reader.onerror = (error): void => {
        reject(error);
      };

      reader.readAsDataURL(fileInput);
    });
  }

  public sendFastpayRequest(freightId: UUID, discount: number): Promise<void> {
    return this.freightCarrierCommandService.requestPayInAdvance({
      id: freightId,
      discount: discount,
    } as RequestPayInAdvanceCommand);
  }

  /**
   * Assigns (granularly) a driver and/or the tractor/trailer vehicles to a freight using
   * `UpdateFreightDriverAndVehiclesAsCarrierCommand`:
   * - driver → `driverContactId`
   * - tractor/box → `motorizedVehicleId`
   * - trailer → `trailerVehicleId`
   *
   * An unselected option (`null`) is sent explicitly to unassign.
   */
  public async assignVehiclesAndDriver(
    freight: IFreightBasicForCarrier,
    driver: IAssignationDriverOption | null = null,
    tractor: IAssignationVehicleOption | null = null,
    trailer: IAssignationVehicleOption | null = null,
    changeDriver = true,
  ): Promise<void> {
    const command = {
      freightId: freight.id,
      // Drivers (INTERACT_WITH_STOPS) cannot change the driver: the property is omitted.
      ...(changeDriver ? { driverContactId: driver && !driver.isNew ? driver.id : null } : {}),
      motorizedVehicleId: tractor && !tractor.isNew ? tractor.id : null,
      trailerVehicleId: trailer && !trailer.isNew ? trailer.id : null,
    } as unknown as UpdateFreightDriverAndVehiclesAsCarrierCommand;

    return this.freightCarrierCommandService.updateFreightDriverAndVehiclesAsCarrier(command);
  }

  private async handleCommand(commandPromise: Promise<void>): Promise<void> {
    await commandPromise.catch(async (error: unknown) => {
      // TODO implement error handling (old app only logged it)
      console.warn(error);
    });
  }

  private getFileFormattedDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}${month}${day}_${hours}${minutes}`;
  }
}
